/**
 * What open portal tabs tell each other (TASK-649, TASK-637).
 *
 * The session lives in each tab's memory and sessionStorage, never in
 * localStorage. A new tab asks the open tabs for it. When a session ends,
 * every tab that holds it hears so and signs out too.
 *
 * Each sign-in has its own id. A message about one sign-in never ends
 * another, so a stale tab that wakes with an old, revoked session cannot sign
 * out a person who has since signed in again.
 */
import type { EndReason } from './limits';

const CHANNEL_NAME = 'portal-auth';
const SIGNED_OUT_KEY = 'portal_signed_out';
const REASON_KEY = 'portal_signout_reason';
/** Kept through the sign-out clean-up of sessionStorage. */
const KEEP_IN_SESSION_STORAGE = [REASON_KEY, 'csp-preload-reloaded'];
/** How many ended sign-ins to remember. */
const MARKER_LIMIT = 10;

export type TabMessage<S> =
  | { type: 'request'; id: string }
  | { type: 'session'; id: string; session: S }
  | { type: 'ended'; sid: string; reason: EndReason }
  /** The person confirmed it is them: fresh tokens for every tab that holds this sign-in. */
  | { type: 'updated'; session: S };

export interface TabLink<S> {
  post(message: TabMessage<S>): void;
  listen(handler: (message: TabMessage<S>) => void): () => void;
}

/** A link over BroadcastChannel. With no BroadcastChannel, each tab is on its own. */
export function broadcastLink<S>(): TabLink<S> {
  if (typeof BroadcastChannel === 'undefined') {
    return { post: () => undefined, listen: () => () => undefined };
  }
  const channel = new BroadcastChannel(CHANNEL_NAME);
  // Node (tests) keeps a process alive while a channel is open. Browsers have no unref.
  (channel as BroadcastChannel & { unref?: () => void }).unref?.();
  return {
    post: (message) => channel.postMessage(message),
    listen: (handler) => {
      const onMessage = (event: MessageEvent) => handler(event.data as TabMessage<S>);
      channel.addEventListener('message', onMessage);
      return () => channel.removeEventListener('message', onMessage);
    },
  };
}

interface EndedMarker {
  sid: string;
  reason: EndReason;
  at: number;
}

function readMarkers(): EndedMarker[] {
  try {
    const raw = localStorage.getItem(SIGNED_OUT_KEY);
    const parsed = raw ? (JSON.parse(raw) as unknown) : [];
    return Array.isArray(parsed) ? (parsed as EndedMarker[]) : [];
  } catch {
    return [];
  }
}

/**
 * Mark a sign-in as ended, before any tab is told. A tab that misses the
 * message (asleep, frozen, restored from the back-forward cache) finds the
 * mark the next time it asks for its session, and a tab never hands an
 * ended session to a new tab.
 */
export function markEnded(sid: string, reason: EndReason, now = Date.now()): void {
  const markers = readMarkers();
  // The first reason stands: an idle sign-out is not renamed by the refused refresh that follows it.
  if (markers.some((marker) => marker.sid === sid)) return;
  markers.push({ sid, reason, at: now });
  try {
    localStorage.setItem(SIGNED_OUT_KEY, JSON.stringify(markers.slice(-MARKER_LIMIT)));
  } catch {
    // Storage refused: the broadcast still reaches the open tabs.
  }
}

/** The reason a sign-in ended, or null while it is still live. */
export function endedReason(sid: string): EndReason | null {
  return readMarkers().find((marker) => marker.sid === sid)?.reason ?? null;
}

/** Hear another tab mark a sign-in as ended. */
export function onEndedMark(handler: () => void): () => void {
  const listener = (event: StorageEvent) => {
    if (event.key === SIGNED_OUT_KEY) handler();
  };
  window.addEventListener('storage', listener);
  return () => window.removeEventListener('storage', listener);
}

/** Keep why this tab was signed out, for the sign-in page after the reload. */
export function keepReason(reason: EndReason): void {
  try {
    if (reason === 'manual') sessionStorage.removeItem(REASON_KEY);
    else sessionStorage.setItem(REASON_KEY, reason);
  } catch {
    // No reason shown: the person still sees the sign-in page.
  }
}

export function readReason(): EndReason | null {
  try {
    const value = sessionStorage.getItem(REASON_KEY);
    return value === 'idle' || value === 'max-age' || value === 'expired' ? value : null;
  } catch {
    return null;
  }
}

export function forgetReason(): void {
  try {
    sessionStorage.removeItem(REASON_KEY);
  } catch {
    // Nothing to forget.
  }
}

/**
 * Remove what the signed-in person left in this tab's sessionStorage: the
 * session copy, deploy plans, deploy failures, the account cache. Keeps the
 * sign-out reason.
 */
export function clearTabStorage(): void {
  try {
    for (const key of Object.keys(sessionStorage)) {
      if (!KEEP_IN_SESSION_STORAGE.includes(key)) sessionStorage.removeItem(key);
    }
  } catch {
    // Storage refused.
  }
}
